import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function AssessmentType() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      id: 0,
      name: "",
      passingScorePercentage: "",
      totalScore: "",
      category: "",
      remarks: "",
      createdBy: localStorage.getItem("userId"),
      organizationId: organizationId,
    },
  });

  const [assessmentTypes, setAssessmentTypes] = useState([]);

  useEffect(() => {
    const fetchAssessmentTypes = async () => {
      try {
        const response = await get(
          `AssessmentType/GetAssessmentTypeByOrganizationIdAsync?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setAssessmentTypes(response.assessmentType);
      } catch (error) {
        console.error("Error fetching assessmentType:", error);
      }
    };

    fetchAssessmentTypes();
  }, [triggerRerender]);

  const assessmentCategories = [
    {
      id: "Written",
      name: "Written",
    },
    {
      id: "Oral",
      name: "Oral",
    },
    {
      id: "Practical",
      name: "Practical",
    },
  ];

  const columns = [
    {
      accessorKey: "mrt-row-numbers",
      header: "Sr No.",
      enableColumnOrdering: false,
      enableEditing: false,
      enableSorting: false,
      size: 80,
      Cell: ({ row }) => row.index + 1,
    },
    {
      header: "Name",
      accessorKey: "name",
    },
    {
      header: "Passing Score (%)",
      accessorKey: "passingScorePercentage",
    },
    {
      header: "Total Score",
      accessorKey: "totalScore",
    },
    {
      id: "actions",
      header: "Action",
      size: 120,
      Cell: ({ row }) => (
        <button
          type="button"
          className="btn p-0"
          onClick={() => handleEditClick(row.original.id)}
        >
          <i className="fas fa-pencil"></i>
        </button>
      ),
    },
  ];

  const exportToExcel = () => {
    const filteredData = assessmentTypes.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
      "Passing Score (%)": row.passingScorePercentage,
      "Total Score": row.totalScore,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AssessmentTypes");
    XLSX.writeFile(workbook, "AssessmentTypes.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    // doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Assessment Types List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const headers = [["Sr No", "Name", "Passing Score (%)", "Total Score"]];

    const bodyData = assessmentTypes.map((row, index) => [
      index + 1,
      row.name,
      row.passingScorePercentage,
      row.totalScore,
    ]);

    doc.autoTable({
      head: headers,
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("AssessmentTypes.pdf");
  };

  const handleEditClick = async (rowId) => {
    const singleAssessmentType = await get(
      `AssessmentType/GetAssessmentTypeByIdAsync?assessmentTypeId=${rowId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const singleAssessmentTypeData = singleAssessmentType.assessmentType;

    reset({
      id: singleAssessmentTypeData.id,
      name: singleAssessmentTypeData.name,
      passingScorePercentage: singleAssessmentTypeData.passingScorePercentage,
      totalScore: singleAssessmentTypeData.totalScore,
      category: singleAssessmentTypeData.category,
      remarks: singleAssessmentTypeData.remarks,
      organizationId: singleAssessmentTypeData.organizationId,
      createdBy: singleAssessmentTypeData.createdBy,
      updatedBy: localStorage.getItem("userId"),
    });
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await post(
        "AssessmentType/AddUpdateAssessmentTypeAsync",
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          id: 0,
          name: "",
          passingScorePercentage: "",
          totalScore: "",
          remarks: "",
          createdBy: localStorage.getItem("userId"),
          organizationId: organizationId,
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Assessment Type</h2>
        {/* <div className="ms-auto">
          <Link
            to="/organization-form"
            className="btn btn-outline-primary btn-sm"
          >
            Add New
          </Link>
        </div> */}
      </div>

      <form
        className="row g-3 new-form-style"
        onSubmit={handleSubmit(saveData)}
      >
        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              <div className="row gy-3 gx-2">
                <div className="col-md-12">
                  <label className="form-label form-label-new">
                    Name<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`name`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("name")}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Passing Score (%)
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`passingScorePercentage`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="number"
                    className="form-control"
                    onInput={(e) => {
                      if (e.target.value < 0) {
                        e.target.value = 0;
                      }
                    }}
                    onChange={handleChangeOfInputWithLengthCheck(
                      "passingScorePercentage"
                    )}
                  />

                  {errors.passingScorePercentage && (
                    <small className="text-danger">
                      {errors.passingScorePercentage.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Total Score<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`totalScore`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="number"
                    className="form-control"
                    onInput={(e) => {
                      if (e.target.value < 0) {
                        e.target.value = 0;
                      }
                    }}
                    onChange={handleChangeOfInputWithLengthCheck("totalScore")}
                  />
                  {errors.totalScore && (
                    <small className="text-danger">
                      {errors.totalScore.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">Category</label>
                  <Controller
                    name="category"
                    control={control}
                    // rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <select
                        {...field}
                        className="form-select"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <option value="" key={"category-key"}>
                          Select
                        </option>
                        {assessmentCategories.map((categoryItem) => (
                          <option key={categoryItem.id} value={categoryItem.id}>
                            {categoryItem.name}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.category && (
                    <small className="text-danger">
                      {errors.category.message}
                    </small>
                  )}
                </div>
                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-7">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={assessmentTypes}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{ columnPinning: { right: ["actions"] } }}
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default AssessmentType;
